<script setup lang="ts">
  import { onMounted, provide, ref } from 'vue';
  import { useRouter } from 'vue-router';
  import { useModal } from 'vue-final-modal';

  import { Can } from '@/entities/Permission';
  import type { DocumentSendFilters } from '@/entities/DocumentSend';
  import { useTableFilterModal, type OrderOptions } from '@/shared/lib';
  import { SortOrderInjectionKey } from '@/shared/lib/sortOrder';
  import {
    ColoredButton,
    ConfirmDeleteModal,
    PaginateOrScroll,
    SearchBar,
    SearchToolbar,
    SelectInput,
  } from '@/shared/ui';
  import SendArrowIcon from '@/shared/assets/icons/SendArrow.svg';
  import ListIcon from '@/shared/assets/icons/ListIcon.svg';
  import ExportExcelIcon from '@/shared/assets/icons/ExportExcel.svg';

  import { useDocumentSendControlList } from '../model/useDocumentSendControlList';
  import { useDocumentSendControlExport } from '../model/useDocumentSendControlExport';
  import DocumentSendControlFilterModal from './DocumentSendControlFilterModal.vue';
  import DocumentSendControlTable from './DocumentSendControlTable.vue';
  import DocumentSendDetailModal from './DocumentSendDetailModal.vue';

  const router = useRouter();

  const DOCUMENT_SEND_ORDER_OPTIONS: OrderOptions[] = [
    { label: 'Por título de A a Z', sort: 'title', order: 'asc' },
    { label: 'Por título de Z a A', sort: 'title', order: 'desc' },
    { label: 'Por envio mais recente', sort: 'sendDate', order: 'desc' },
    { label: 'Por envio mais antigo', sort: 'sendDate', order: 'asc' },
  ];

  const documentSendReportPermissions = [
    { action: 'manage' as const, subject: 'DocumentManagement' as const },
    { action: 'send' as const, subject: 'Document' as const },
  ];

  const documentSendCreatePermissions = [{ action: 'send' as const, subject: 'Document' as const }];

  const {
    allDocumentSends,
    documentSends,
    filters,
    handleDeleteDocumentSend,
    handleFiltersApplied,
    handleSortOptions,
    hasMoreItems,
    handleItemsPerPageChange,
    handlePageChanged,
    handleSearch,
    hasLoaded,
    isFilterActive,
    itemsPerPageOptions,
    loadDocumentSends,
    loading,
    loadingMore,
    metadata,
    order,
    searchTerm,
    selectedItemsPerPage,
    sort,
    shouldShowEmptyState,
    shouldShowNoResults,
  } = useDocumentSendControlList();

  const { handleExportToExcel, isExportDisabled, isExporting } = useDocumentSendControlExport({
    searchTerm,
    sort,
    order,
    filters,
    metadata,
    hasLoaded,
  });

  provide(SortOrderInjectionKey, {
    sort,
    order,
  });

  const pendingDeletionId = ref<number | null>(null);

  const { open: openDeleteConfirmation, close: closeDeleteConfirmation } = useModal({
    component: ConfirmDeleteModal,
    attrs: {
      title: 'Tem certeza que deseja excluir o envio?',
      description: 'A ação não pode ser desfeita.',
      onClose: () => {
        pendingDeletionId.value = null;
        closeDeleteConfirmation();
      },
      onConfirm: async () => {
        const id = pendingDeletionId.value;
        pendingDeletionId.value = null;
        closeDeleteConfirmation();
        if (id !== null) {
          await handleDeleteDocumentSend(id);
        }
      },
    },
  });

  const handleDeleteRequested = (id: number) => {
    pendingDeletionId.value = id;
    openDeleteConfirmation();
  };

  const detailModal = useModal({
    component: DocumentSendDetailModal,
    attrs: {
      documentSendId: null,
      onClose: () => {
        detailModal.close();
      },
    },
  });

  const handleViewRequested = (id: number) => {
    detailModal.patchOptions({
      attrs: {
        ...detailModal.options.attrs,
        documentSendId: id,
      },
    });
    detailModal.open();
  };

  const { openFilterModal } = useTableFilterModal<DocumentSendFilters>(
    DocumentSendControlFilterModal,
    {
      clearFilters: () => handleFiltersApplied({}),
      filters: filters.value,
    },
    (nextFilters) => {
      void handleFiltersApplied(nextFilters);
    },
    () => filters.value
  );

  onMounted(() => {
    void loadDocumentSends();
  });

  const handleCreateSend = () => {
    const navigation = router.push('/gestao-documentos/controle-envio/enviar');
    navigation?.catch(() => undefined);
  };

  const handleViewSendReport = () => {
    const navigation = router.push('/gestao-documentos/controle-envio/lista-envio');
    navigation?.catch(() => undefined);
  };
</script>

<template>
  <section
    class="flex flex-col gap-6"
    data-testid="document-send-control-list"
  >
    <div class="flex flex-wrap gap-4">
      <Can :permissions="documentSendCreatePermissions">
        <ColoredButton
          text="Enviar"
          :icon="SendArrowIcon"
          icon-alt="Enviar"
          @click="handleCreateSend"
        />
      </Can>
      <Can :permissions="documentSendReportPermissions">
        <ColoredButton
          text="Lista de envios"
          :icon="ListIcon"
          icon-alt="Lista de envios"
          @click="handleViewSendReport"
        />
      </Can>
      <ColoredButton
        text="Exportar para Excel"
        :icon="ExportExcelIcon"
        icon-alt="Exportar para Excel"
        :disabled="isExportDisabled"
        :loading="isExporting"
        @click="handleExportToExcel"
      />
    </div>

    <SearchToolbar>
      <template #search>
        <div
          class="w-full"
          data-testid="document-send-control-search"
        >
          <SearchBar
            :initial-search-term="searchTerm"
            @search="handleSearch"
          />
        </div>
      </template>
      <template #quantity>
        <SelectInput
          :model-value="selectedItemsPerPage"
          :options="itemsPerPageOptions"
          button-class="bg-white-01 border border-gray-02 whitespace-nowrap shadow-xs"
          @update:model-value="handleItemsPerPageChange"
        />
      </template>
    </SearchToolbar>

    <PaginateOrScroll
      :items="documentSends"
      :all-items="allDocumentSends"
      :metadata="metadata"
      :selected-quantity="selectedItemsPerPage"
      :loading-more="loadingMore"
      :has-more-items="hasMoreItems"
      entity-name="envio"
      @page-changed="handlePageChanged"
    >
      <template #table="{ items: tableItems }">
        <DocumentSendControlTable
          :document-sends="tableItems"
          :show-loading-state="loading && !hasLoaded"
          :should-show-empty-state="shouldShowEmptyState"
          :should-show-no-results="shouldShowNoResults"
          :order-options="DOCUMENT_SEND_ORDER_OPTIONS"
          :is-filter-active="isFilterActive"
          @update:sort="handleSortOptions"
          @filter-clicked="openFilterModal"
          @view-clicked="handleViewRequested"
          @delete-clicked="handleDeleteRequested"
        />
      </template>
    </PaginateOrScroll>
  </section>
</template>
